import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  Bell,
  Check,
  CreditCard,
  Copy,
  Plus,
  Search,
  Wallet,
} from "lucide-react";

import Banner from "./common/Banner";
import CompleteActivation from "./common/CompleteActivation";
import InitiateTransfer from "./common/InitiateTransfer";
import BulkTransfer from "./common/BulkTransfer";
import TransferDetails from "./common/TransferDetails";
import { getTransferHistory, getUser, getUserWallet } from "src/services";
import { formatAmount } from "src/helpers/utils";
import { Wrapper } from "./Styles";

const hasStoredAuth = () => Boolean(localStorage.getItem("access") || localStorage.getItem("refresh"));

type WalletItem = {
  balance?: string | number;
  blocked_balance?: string | number;
  available_balance?: string | number;
  wallet_balance?: string | number;
  current_balance?: string | number;
  amount?: string | number;
  currency?: string;
  currency_code?: string;
  currency_value?: string;
  number?: string;
  wallet_number?: string;
  wallet_no?: string;
  account_number?: string;
  id?: string | number;
};

type TransactionItem = {
  amount?: string | number;
  currency_code?: string;
  transaction_type?: string;
  status?: string;
  debit_or_credit?: string;
  reference?: string;
  channel?: string;
  description?: string;
  created_at?: string;
  date_created?: string;
  sender_wallet_number?: string;
  receiver_wallet_number?: string;
};

const maskWalletNumber = (value?: string) => {
  if (!value) {
    return "Wallet number unavailable";
  }

  const cleaned = value.toString().replace(/\s+/g, "");
  if (cleaned.length <= 4) {
    return cleaned;
  }

  return `•••• ${cleaned.slice(-4)}`;
};

const getWalletBalance = (wallet?: WalletItem) => {
  const balance =
    wallet?.balance ??
    wallet?.available_balance ??
    wallet?.wallet_balance ??
    wallet?.current_balance ??
    wallet?.amount ??
    0;
  const blocked = Number(wallet?.blocked_balance ?? 0);
  const available = Number(balance) - blocked;

  return formatAmount(String(Math.max(available, 0)));
};

const getWalletBlockedBalance = (wallet?: WalletItem) => {
  return Number(wallet?.blocked_balance ?? 0);
};

const getWalletCurrency = (wallet?: WalletItem, index?: number) => {
  return wallet?.currency ?? wallet?.currency_code ?? wallet?.currency_value ?? `Wallet ${index}`;
};

const getWalletNumber = (wallet?: WalletItem) => {
  return wallet?.number ?? wallet?.wallet_number ?? wallet?.wallet_no ?? wallet?.account_number;
};

const formatShortDate = (value?: string) => {
  if (!value) {
    return "Recent";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const getTransactionCounterparty = (transaction?: TransactionItem, isCredit?: boolean) => {
  return (
    transaction?.reference ||
    transaction?.description ||
    transaction?.channel ||
    (isCredit ? transaction?.sender_wallet_number : transaction?.receiver_wallet_number) ||
    "Transfer"
  );
};

const getStatusTone = (status?: string) => {
  const normalized = (status || "").toLowerCase();

  if (normalized.includes("completed") || normalized.includes("success")) {
    return "bg-emerald-50 text-emerald-700 ring-emerald-100";
  }

  if (normalized.includes("pending")) {
    return "bg-amber-50 text-amber-700 ring-amber-100";
  }

  if (normalized.includes("failed") || normalized.includes("cancel")) {
    return "bg-rose-50 text-rose-700 ring-rose-100";
  }

  return "bg-slate-100 text-slate-600 ring-slate-200";
};

const Dashboard = () => {
  const [openActivation, setOpenActivation] = useState(false);
  const [openInitiateTransfer, setOpenInitiateTransfer] = useState(false);
  const [openBulkTransfer, setOpenBulkTransfer] = useState(false);
  const [openTransferDetail, setOpenTransferDetail] = useState(false);
  const [copiedWalletNumber, setCopiedWalletNumber] = useState<string | null>(null);
  const [selectedRowItem, setSelectedRowItem] = useState<TransactionItem | null>(null);

  const {
    data: userData,
    isLoading: userIsLoading,
    refetch: refetchUserData,
  } = useQuery({
    queryKey: ["userDetails"],
    queryFn: () => getUser(),
    enabled: hasStoredAuth(),
  });

  const {
    data: walletData,
    isLoading: walletsAreLoading,
    isError: walletsHaveError,
    refetch: refetchWallets,
  } = useQuery({
    queryKey: ["userWallets", userData?.id],
    queryFn: () => getUserWallet(String(userData?.id)),
    enabled: Boolean(userData?.id),
    retry: false,
  });

  const {
    data: transferHistory,
    isLoading: historyIsLoading,
    isError: historyIsError,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: ["transferHistory", userData?.id],
    queryFn: () => getTransferHistory(userData?.id, 1, {}),
    enabled: !!userData?.id,
  });

  const wallets: WalletItem[] = (walletData ?? []) as WalletItem[];
  const walletCards = wallets;
  const transactions: TransactionItem[] = (transferHistory?.results ?? []) as TransactionItem[];
  const recentTransactions = transactions.slice(0, 5);
  const isPendingActivation = !(
    userData?.user_images?.profile_picture && userData?.user_images?.user_id_image
  );

  const totalBalance = wallets.reduce((sum, wallet) => {
    const balance = Number(wallet?.balance ?? 0);
    return sum + (Number.isNaN(balance) ? 0 : balance);
  }, 0);

  const currencyCount = new Set(
    wallets.map((wallet) => wallet?.currency ?? wallet?.currency_code).filter(Boolean),
  ).size;

  const handleCardClick = (callback?: () => void) => {
    if (isPendingActivation) {
      setOpenActivation(true);
      return;
    }

    callback?.();
  };

  const handleTransactionClick = (item: TransactionItem) => {
    setSelectedRowItem(item);
    setOpenTransferDetail(true);
  };

  const handleCopyWalletNumber = async (walletNumber?: string) => {
    if (!walletNumber) {
      return;
    }

    try {
      await navigator.clipboard.writeText(walletNumber);
      setCopiedWalletNumber(walletNumber);
      window.setTimeout(() => setCopiedWalletNumber((current) => (current === walletNumber ? null : current)), 1500);
    } catch (error) {
      const message = "Unable to copy wallet number";
      console.error(message, error);
    }
  };

  const handleRetryHistory = async () => {
    await refetchUserData();
    await refetchWallets();
    await refetchHistory();
  };

  const initials = (userData?.first_name || userData?.last_name || userData?.email || "H")
    .slice(0, 2)
    .toUpperCase();

  const summaryCards = [
    {
      label: "Total balance",
      value: formatAmount(String(totalBalance)),
      helper: `${wallets.length} wallet${wallets.length === 1 ? "" : "s"}`,
      icon: ArrowUpRight,
    },
    {
      label: "Wallets",
      value: String(wallets.length),
      helper: "Active",
      icon: Wallet,
    },
    {
      label: "Transactions",
      value: String(transferHistory?.count ?? transactions.length ?? 0),
      helper: "Recent activity",
      icon: CreditCard,
    },
    {
      label: "Currencies",
      value: String(currencyCount),
      helper: "Available balances",
      icon: ArrowDownRight,
    },
  ];

  return (
    <Wrapper>
      {isPendingActivation && <Banner />}

      <div className="space-y-6">
        <header className="flex flex-col gap-4 rounded-[32px] border border-white/60 bg-white/80 px-5 py-5 shadow-[0_20px_60px_rgba(15,23,42,0.06)] backdrop-blur md:px-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-2">
              <h1 className="text-3xl font-black tracking-tight text-slate-900 md:text-4xl">
                Dashboard
              </h1>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex items-center gap-3 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-400 shadow-sm">
                <Search className="h-4 w-4" />
                <span>Search</span>
              </div>

              <button
                type="button"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm"
                aria-label="Notifications"
              >
                <Bell className="h-4 w-4" />
              </button>

              <div className="flex items-center gap-3 rounded-full border border-slate-200 bg-white px-3 py-2 shadow-sm">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-orange-400 text-sm font-bold text-white">
                  {initials}
                </div>
                <div className="hidden sm:block">
                  <p className="text-sm font-semibold text-slate-900">
                    {userData?.first_name || userData?.last_name || "My account"}
                  </p>
                  <p className="text-xs text-slate-400">{userData?.email}</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {userIsLoading && !userData ? (
          <div className="rounded-[28px] border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
            Loading dashboard...
          </div>
        ) : (
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.9fr)]">
            <div className="space-y-6">
              <section className="rounded-[32px] border border-slate-200 bg-white/80 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.06)] backdrop-blur">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">My wallets</h2>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-600">
                      {wallets.length} wallet{wallets.length === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>

                <div className="mt-5">
                  {walletsAreLoading && (
                    <div className="rounded-[24px] border border-slate-100 bg-slate-50 px-5 py-10 text-center text-sm text-slate-500">
                      Loading wallets...
                    </div>
                  )}
                  {walletsHaveError && (
                    <div className="rounded-[24px] border border-rose-100 bg-rose-50 px-5 py-10 text-center text-sm text-rose-700">
                      Wallets are temporarily unavailable. Please try again.
                    </div>
                  )}
                  {!walletsAreLoading && !walletsHaveError && walletCards.length === 0 && (
                    <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50 px-5 py-10 text-center text-sm text-slate-500">
                      No wallets have been provisioned for this account yet.
                    </div>
                  )}
                  <div className="flex flex-col gap-4 sm:flex-row sm:gap-4 sm:overflow-x-auto sm:pb-3 sm:pr-1 sm:[scrollbar-width:thin]">
                    {walletCards.map((wallet, index) => {
                      const currency = getWalletCurrency(wallet, index + 1);
                      const balance = getWalletBalance(wallet);
                      const cardStyle =
                        index === 0
                          ? "bg-gradient-to-br from-rose-600 via-rose-700 to-red-950 text-white"
                          : index === 1
                            ? "bg-gradient-to-br from-slate-100 via-white to-slate-200 text-slate-900"
                            : "bg-gradient-to-br from-zinc-900 via-slate-800 to-slate-600 text-white";
                      const helperTone =
                        index === 1 ? "text-slate-500" : index === 0 ? "text-white/70" : "text-white/60";
                      const numberTone =
                        index === 1 ? "text-slate-700" : index === 0 ? "text-white/90" : "text-white/80";

                      return (
                        <article
                          key={wallet?.id ?? `${wallet?.currency ?? currency}-${wallet?.number ?? index}`}
                          className={`relative h-[220px] w-full sm:w-[clamp(420px,60vw,560px)] sm:flex-shrink-0 overflow-hidden rounded-[24px] border p-5 shadow-[0_18px_40px_rgba(15,23,42,0.12)] transition duration-200 hover:-translate-y-0.5 ${cardStyle} ${
                            index === 1 ? "border-slate-200" : "border-white/10"
                          }`}
                        >
                          <div
                            className={`absolute -right-10 -top-10 h-32 w-32 rounded-full ${
                              index === 1 ? "bg-white/70" : "bg-white/10"
                            } blur-3xl`}
                          />
                          <div className="relative flex h-full flex-col justify-between">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <p
                                  className={`text-[11px] font-semibold uppercase tracking-[0.24em] ${helperTone}`}
                                >
                                  {currency}
                                </p>
                                <p
                                  className={`mt-2 text-xs font-medium ${
                                    index === 1 ? "text-slate-600" : "text-white/75"
                                  }`}
                                >
                                {`Wallet #${index + 1}`}
                              </p>
                            </div>

                            <div
                              className={`flex h-10 w-10 items-center justify-center rounded-full ${
                                index === 1 ? "bg-white text-slate-900" : index === 0 ? "bg-white/15 text-white" : "bg-white/10 text-white"
                              }`}
                            >
                              <Wallet className="h-4 w-4" />
                            </div>
                          </div>

                            <div className="mt-6">
                              <p className={`text-[11px] font-medium ${helperTone}`}>Available</p>
                              <p className="mt-1 text-2xl font-black tracking-tight">
                                {balance}
                              </p>
                              {getWalletBlockedBalance(wallet) > 0 && (
                                <p className={`mt-0.5 text-[11px] ${helperTone}`}>
                                  In trade: {formatAmount(getWalletBlockedBalance(wallet))}
                                </p>
                              )}
                            </div>

                            <div className="flex items-end justify-between gap-4">
                              <div>
                                <p
                                  className={`text-[10px] uppercase tracking-[0.18em] ${helperTone}`}
                                >
                                  Wallet number
                                </p>
                                <p className={`mt-1 font-mono text-sm font-semibold ${numberTone}`}>
                                  {maskWalletNumber(getWalletNumber(wallet))}
                                </p>
                              </div>

                              <button
                                type="button"
                                onClick={() => handleCopyWalletNumber(getWalletNumber(wallet))}
                                disabled={!getWalletNumber(wallet)}
                                className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-[11px] font-semibold transition ${
                                  index === 1
                                    ? "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                                    : "border-white/15 bg-white/10 text-white hover:bg-white/15"
                                } disabled:cursor-not-allowed disabled:opacity-60`}
                              >
                                {copiedWalletNumber === getWalletNumber(wallet) ? (
                                  <>
                                    <Check className="h-3.5 w-3.5" />
                                    Copied
                                  </>
                                ) : (
                                  <>
                                    <Copy className="h-3.5 w-3.5" />
                                    Copy
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              </section>

              <section className="rounded-[32px] border border-slate-200 bg-white/80 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.06)] backdrop-blur">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Recent transactions</h2>
                  </div>

                  <Link
                    to="/home/history"
                    className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
                  >
                    View history
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>

                <div className="mt-5">
                  {historyIsLoading && transactions.length === 0 ? (
                    <div className="space-y-3">
                      {Array.from({ length: 4 }).map((_, index) => (
                        <div
                          key={`txn-skeleton-${index}`}
                          className="rounded-[24px] border border-slate-100 bg-slate-50/80 px-4 py-4"
                        >
                          <div className="flex items-center gap-4">
                            <div className="h-12 w-12 rounded-2xl bg-slate-200/80" />
                            <div className="min-w-0 flex-1 space-y-2">
                              <div className="h-3 w-1/3 rounded-full bg-slate-200/80" />
                              <div className="h-2.5 w-1/2 rounded-full bg-slate-200/60" />
                            </div>
                            <div className="space-y-2 text-right">
                              <div className="ml-auto h-3 w-20 rounded-full bg-slate-200/80" />
                              <div className="ml-auto h-6 w-16 rounded-full bg-slate-200/60" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : historyIsError ? (
                    <div className="rounded-[28px] border border-rose-200 bg-rose-50 px-5 py-6 text-sm text-rose-700">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-base font-semibold">Recent activity is unavailable.</p>
                          <p className="mt-1 text-sm text-rose-600/80">
                            We could not fetch the latest transfer history from the payment service.
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() => {
                            void handleRetryHistory();
                          }}
                          className="inline-flex items-center justify-center rounded-full border border-rose-200 bg-white px-4 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-50"
                        >
                          Retry
                        </button>
                      </div>
                    </div>
                  ) : recentTransactions.length === 0 ? (
                    <div className="rounded-[28px] border border-dashed border-slate-200 bg-slate-50/80 px-5 py-8 text-sm text-slate-500">
                      No recent transfers yet. Once activity starts, the latest transactions will
                      appear here.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {recentTransactions.map((transaction, index) => {
                        const isCredit = (transaction?.debit_or_credit || "").toLowerCase() === "credit";
                        const amount = formatAmount(String(transaction?.amount ?? 0));
                        const statusTone = getStatusTone(transaction?.status);
                        const counterParty = getTransactionCounterparty(transaction, isCredit);
                        const displayDate = formatShortDate(
                          transaction?.created_at || transaction?.date_created,
                        );

                        return (
                          <button
                            key={
                              transaction?.reference ??
                              `${transaction?.transaction_type ?? "transaction"}-${index}`
                            }
                            type="button"
                            onClick={() => handleTransactionClick(transaction)}
                            className="group flex w-full items-center gap-4 rounded-[24px] border border-slate-100 bg-white/90 px-4 py-4 text-left shadow-[0_10px_30px_rgba(15,23,42,0.04)] transition hover:-translate-y-0.5 hover:border-rose-200 hover:shadow-[0_16px_40px_rgba(15,23,42,0.08)]"
                          >
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm ring-4 ring-slate-50">
                              {isCredit ? (
                                <ArrowDownRight className="h-4 w-4 text-emerald-400" />
                              ) : (
                                <ArrowUpRight className="h-4 w-4 text-rose-300" />
                              )}
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-semibold text-slate-900">
                                    {transaction?.transaction_type || "Transfer"}
                                  </p>
                                  <p className="mt-1 truncate text-xs text-slate-500">
                                    {counterParty}
                                  </p>
                                </div>

                                <div className="flex shrink-0 flex-col items-end gap-2 text-right">
                                  <p
                                    className={`text-sm font-black tracking-tight ${
                                      isCredit ? "text-emerald-600" : "text-rose-500"
                                    }`}
                                  >
                                    {isCredit ? "+" : "-"}
                                    {amount} {transaction?.currency_code}
                                  </p>
                                  <span
                                    className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ${statusTone}`}
                                  >
                                    {transaction?.status || "Pending"}
                                  </span>
                                </div>
                              </div>

                              <div className="mt-3 flex items-center justify-between gap-3 text-[11px] uppercase tracking-[0.22em] text-slate-400">
                                <span>{displayDate}</span>
                                <span>{transaction?.channel || "Payment"}</span>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </section>
            </div>

            <aside className="space-y-6">
              <section className="rounded-[32px] border border-slate-200 bg-white/80 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.06)] backdrop-blur">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Overview</h2>
                  </div>
                  <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                    Live
                  </div>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  {summaryCards.map(({ label, value, helper, icon: Icon }) => (
                    <div
                      key={label}
                      className="rounded-[24px] border border-slate-100 bg-slate-50/80 p-4 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-medium uppercase tracking-[0.28em] text-slate-400">
                            {label}
                          </p>
                          <p className="mt-3 text-xl font-black tracking-tight text-slate-900">
                            {value}
                          </p>
                        </div>
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-rose-500 shadow-sm ring-1 ring-slate-100">
                          <Icon className="h-4 w-4" />
                        </div>
                      </div>
                      <p className="mt-3 text-xs text-slate-500">{helper}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-[32px] border border-slate-200 bg-white/80 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.06)] backdrop-blur">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Quick actions</h2>
                  </div>

                  <div className="rounded-full bg-rose-50 p-2 text-rose-500">
                    <Plus className="h-4 w-4" />
                  </div>
                </div>

                <div className="mt-5 space-y-3">
                  <button
                    type="button"
                    onClick={() => handleCardClick(() => setOpenInitiateTransfer(true))}
                    className="flex w-full items-center justify-between rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-4 text-left transition hover:bg-white"
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-900">New transfer</p>
                      <p className="mt-1 text-xs text-slate-500">
                        Send money securely with your selected wallet.
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-rose-500" />
                  </button>

                  <button
                    type="button"
                    onClick={() => handleCardClick(() => setOpenBulkTransfer(true))}
                    className="flex w-full items-center justify-between rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-4 text-left transition hover:bg-white"
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Bulk transfer</p>
                      <p className="mt-1 text-xs text-slate-500">
                        Add multiple recipients in one batch.
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-rose-500" />
                  </button>

                  <Link
                    to="/home/wallets"
                    className="flex items-center justify-between rounded-[22px] border border-slate-200 bg-white px-4 py-4 transition hover:bg-slate-50"
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Wallet settings</p>
                      <p className="mt-1 text-xs text-slate-500">Review or add linked wallets.</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-500" />
                  </Link>
                </div>
              </section>
            </aside>
          </div>
        )}
      </div>

      <CompleteActivation
        open={openActivation}
        description="Complete activation to enable transfer initiation."
        handleClose={() => setOpenActivation(false)}
      />
      <InitiateTransfer
        open={openInitiateTransfer}
        handleClose={() => setOpenInitiateTransfer(false)}
      />
      <TransferDetails
        open={openTransferDetail}
        handleClose={() => setOpenTransferDetail(false)}
        details={selectedRowItem}
      />
      <BulkTransfer open={openBulkTransfer} handleClose={() => setOpenBulkTransfer(false)} />
    </Wrapper>
  );
};

export default Dashboard;
